<template>
  <div class="notification-bell">
    <button 
      @click="toggleDropdown"
      class="bell-btn"
      :class="{ 'has-notifications': unreadCount > 0 }"
    >
      🔔
      <span v-if="unreadCount > 0" class="notification-count">
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
    </button>

    <div v-if="dropdownOpen" class="notification-dropdown">
      <div class="dropdown-header">
        <h4>Notifications</h4>
        <button 
          v-if="notifications.length > 0"
          @click="$emit('clear-all')"
          class="clear-all-btn"
        >
          Clear All
        </button>
      </div>
      
      <div class="notification-list">
        <div 
          v-for="notification in notifications"
          :key="notification.id"
          @click="handleNotificationClick(notification)"
          class="notification-item"
          :class="notification.type"
        >
          <div class="notification-icon">
            {{ getNotificationIcon(notification.type) }}
          </div>
          <div class="notification-content">
            <div class="notification-title">{{ notification.title }}</div>
            <div class="notification-message">{{ notification.message }}</div>
            <div class="notification-time">
              {{ formatTime(notification.timestamp) }}
            </div>
          </div>
          <button 
            @click.stop="removeNotification(notification.id)"
            class="close-btn"
          >
            ×
          </button>
        </div>
        
        <div v-if="notifications.length === 0" class="empty-state">
          No notifications
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    notifications: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      dropdownOpen: false
    }
  },
  
  computed: {
    unreadCount() {
      return this.notifications.filter(n => !n.read).length
    }
  },
  
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
    },
    
    handleNotificationClick(notification) {
      this.$emit('notification-click', notification)
    },
    
    removeNotification(notificationId) {
      this.$emit('notification-remove', notificationId)
    },
    
    getNotificationIcon(type) {
      const icons = {
        success: '✅',
        warning: '⚠️',
        error: '❌',
        emergency: '🚨',
        info: 'ℹ️'
      }
      return icons[type] || '🔔'
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style scoped>
.notification-bell {
  position: relative;
}

.bell-btn {
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bell-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.bell-btn.has-notifications {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.4);
}

.notification-count {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 0.7em;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.dropdown-header h4 {
  margin: 0;
  color: #374151;
  font-size: 1em;
}

.clear-all-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.8em;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.clear-all-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
}

.notification-item:hover {
  background: #f9fafb;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item.success {
  border-left: 3px solid #10b981;
}

.notification-item.warning {
  border-left: 3px solid #f59e0b;
}

.notification-item.error {
  border-left: 3px solid #ef4444;
}

.notification-item.emergency {
  border-left: 3px solid #dc2626;
  background: #fef2f2;
}

.notification-icon {
  font-size: 1.2em;
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  color: #374151;
  margin-bottom: 2px;
  font-size: 0.9em;
}

.notification-message {
  color: #6b7280;
  font-size: 0.8em;
  line-height: 1.4;
  margin-bottom: 4px;
}

.notification-time {
  color: #9ca3af;
  font-size: 0.7em;
}

.close-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 1.2em;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.empty-state {
  padding: 24px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 0.9em;
}
</style>